import { useEffect } from 'react';
import { useTranslation } from '../../../../i18n/useTranslation';
import { useBulkLicenseActions } from '../../../../hooks/useBulkLicenseActions';
import { LicenseStatus } from '../../../../types/manageLicenses';
import type { ManageLicenseListDto, BulkLicenseActionResultDto } from '../../../../types/manageLicenses';

interface Props {
  open: boolean;
  licenses: ManageLicenseListDto[];
  onClose: () => void;
  onCompleted: (result: BulkLicenseActionResultDto) => void;
}

export default function LicenseReactivateDialog({ open, licenses, onClose, onCompleted }: Props) {
  const { t } = useTranslation();
  const { reactivate } = useBulkLicenseActions();

  // Reset on open (no local state to reset, but keep pattern consistent)
  useEffect(() => {
    if (!open) return;
  }, [open]);

  if (!open) return null;

  const eligible = licenses.filter((l) => l.status === LicenseStatus.Suspended);
  const alreadyActive = licenses.filter((l) => l.status === LicenseStatus.Active);
  const revoked = licenses.filter((l) => l.status === LicenseStatus.Revoked);
  const expired = licenses.filter((l) => l.status === LicenseStatus.Expired);

  const canSubmit = eligible.length > 0 && !reactivate.isPending;

  const submit = async () => {
    const result = await reactivate.mutateAsync(eligible.map((l) => l.id));
    onCompleted(result);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-5">
        <h2 className="text-lg font-semibold mb-2 text-neutral-800">
          {t('App.Admin.Licenses.LicenseReactivateDialog.Title', { count: licenses.length })}
        </h2>
        <p className="text-sm text-neutral-700 mb-3">
          {t('App.Admin.Licenses.LicenseReactivateDialog.Description')}
        </p>

        <div className="mb-4 bg-neutral-50 rounded border border-neutral-200 p-3 text-sm space-y-1">
          <div className="flex justify-between">
            <span className="text-green-700 font-medium">{t('App.Admin.Licenses.LicenseReactivateDialog.EligibleLabel')}</span>
            <span className="font-semibold">{eligible.length}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-neutral-600">{t('App.Admin.Licenses.LicenseReactivateDialog.AlreadyActiveLabel')}</span>
            <span>{alreadyActive.length}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-red-600">{t('App.Admin.Licenses.LicenseReactivateDialog.RevokedLabel')}</span>
            <span>{revoked.length}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-orange-600">{t('App.Admin.Licenses.LicenseReactivateDialog.ExpiredLabel')}</span>
            <span>{expired.length}</span>
          </div>
        </div>

        {eligible.length === 0 && (
          <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2 mb-4">
            {t('App.Admin.Licenses.LicenseReactivateDialog.NoneEligible')}
          </p>
        )}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1 border border-neutral-300 rounded hover:bg-neutral-100"
          >
            {t('App.Admin.Licenses.LicenseReactivateDialog.Cancel')}
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!canSubmit}
            className="px-3 py-1 bg-neutral-800 text-white rounded hover:bg-neutral-900 disabled:opacity-50"
          >
            {reactivate.isPending ? t('App.Admin.Licenses.LicenseReactivateDialog.Reactivating') : t('App.Admin.Licenses.LicenseReactivateDialog.Reactivate', { count: eligible.length })}
          </button>
        </div>
      </div>
    </div>
  );
}
